import { getEventId } from '../selectors/event';
import { TaxExemptionStatus } from '../containers/checkout/constants';
import { CannedQuestionName } from '../api/models/survey';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { validateTaxId, validateQstTaxId } from '../api/taxExemptions';
import { saveOrder as saveOrderApi } from '../api/order';
import { selectors as orderSelectors } from '../reducers/order';
import { cleanFormData } from '../containers/checkout/utils';
import { getFormValues } from 'redux-form';
import { CHECKOUT_FORM_NAME } from '../constants';
import { getAttendeeDeliveryMethodsPayloadForPlacingOrder } from '../selectors/order';
import {
    getSurvey,
    getHasQuebecQSTTaxQuestion,
    getBillingZipCode,
    getAnswersForQuestionsAffectingQuebecQSTTaxes,
} from '../selectors/survey';
import { updateOrderAction, updateTaxExemptionMessageAction } from './order';
import { PaymentMethodType } from '../models/paymentMethods/enums';

const getTaxExemptionFromState = (state) => {
    const {
        order: { costs: { tax = {}, displayTax, cachedDisplayTax } = {} },
    } = state;

    const taxBeforeExemption = tax.taxBeforeExemption;

    return {
        taxBeforeExemption,
        displayTax,
        tax,
        cachedDisplayTax,
    };
};

const applyQuebecQSTTaxExemption = async (dispatch, getState) => {
    const { taxBeforeExemption, cachedDisplayTax } = getTaxExemptionFromState(
        getState(),
    );

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());
    const taxValue = taxBeforeExemption ?? cachedDisplayTax.tax.value;

    orderUpdated.response.costs.displayTax = {
        // For QST, we use the cached value + current tax value
        ...cachedDisplayTax,
        tax: orderUpdated.response.costs.tax,
    }; // re-use the value from the init order
    orderUpdated.response.costs.tax.taxBeforeExemption = taxValue;

    dispatch(updateOrderAction(orderUpdated.response));
    dispatch(
        updateTaxExemptionMessageAction(
            TaxExemptionStatus.validTaxIdAndTaxExemption,
        ),
    );
};

const applyNonGTSTaxExemption = async (dispatch, getState) => {
    const { taxBeforeExemption, displayTax, tax } = getTaxExemptionFromState(
        getState(),
    );

    const hasTaxExemptionApplied = taxBeforeExemption;

    if (hasTaxExemptionApplied) {
        dispatch(
            updateTaxExemptionMessageAction(
                TaxExemptionStatus.validTaxIdAndTaxExemption,
            ),
        );
        return;
    }

    if (!displayTax) {
        dispatch(
            updateTaxExemptionMessageAction(
                TaxExemptionStatus.validTaxIdWithoutTaxExemption,
            ),
        );
        return;
    }

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());

    orderUpdated.response.costs.tax.displayTax = displayTax;
    orderUpdated.response.costs.tax.taxBeforeExemption = tax.value;

    dispatch(updateOrderAction(orderUpdated.response));
    dispatch(
        updateTaxExemptionMessageAction(
            TaxExemptionStatus.validTaxIdAndTaxExemption,
        ),
    );
};

const applyTaxExemption = (dispatch, getState) =>
    getHasQuebecQSTTaxQuestion(getState())
        ? applyQuebecQSTTaxExemption(dispatch, getState)
        : applyNonGTSTaxExemption(dispatch, getState);

const revokeTaxExemption = () => async (dispatch, getState) => {
    const { taxBeforeExemption } = getTaxExemptionFromState(getState());
    const revokesExemption = taxBeforeExemption;

    if (!revokesExemption) {
        return;
    }

    const orderUpdated = await dispatch(saveOrderWithTaxExemption());

    orderUpdated.response.costs.tax.taxBeforeExemption = null;

    dispatch(updateOrderAction(orderUpdated.response));
};

const checkQstTaxesFactory =
    (isLatestCheckQstTaxesCall) => async (dispatch, getState) => {
        const state = getState();
        const {
            form: { checkoutForm: { values: { buyer = {} } = {} } = {} },
        } = state;

        const taxId = buyer[CannedQuestionName.taxRegistrationId];
        const residenceZipCode = buyer[CannedQuestionName.qstZipCode];
        const billingZipCode = getBillingZipCode(state);

        if (isEmpty(residenceZipCode)) {
            dispatch(updateTaxExemptionMessageAction(TaxExemptionStatus.init));
            return;
        }

        dispatch(updateTaxExemptionMessageAction(TaxExemptionStatus.verifying));

        const { is_exempt, is_tax_id_valid } = await validateQstTaxId({
            eventId: getEventId(getState()),
            residenceZipCode,
            billingZipCode,
            taxId,
        });

        if (!isLatestCheckQstTaxesCall()) {
            return;
        }

        if (!is_exempt) {
            await dispatch(revokeTaxExemption());

            const taxExemptionStatus = is_tax_id_valid
                ? TaxExemptionStatus.validTaxIdWithoutTaxExemption
                : TaxExemptionStatus.invalidTaxId;

            dispatch(updateTaxExemptionMessageAction(taxExemptionStatus));

            return;
        }

        try {
            await dispatch(applyTaxExemption);
        } catch (error) {
            await dispatch(revokeTaxExemption());

            dispatch(
                updateTaxExemptionMessageAction(
                    TaxExemptionStatus.validTaxIdWithoutTaxExemption,
                ),
            );
        }
    };

let lastCheckQstTaxesTask = {
    id: 0,
    promise: Promise.resolve(),
};

const checkQstTaxesSequentially = () => (dispatch) => {
    const id = lastCheckQstTaxesTask.id + 1;
    const isLatestCheckQstTaxesCall = () => id === lastCheckQstTaxesTask.id;

    const promise = lastCheckQstTaxesTask.promise.then(() =>
        isLatestCheckQstTaxesCall()
            ? dispatch(checkQstTaxesFactory(isLatestCheckQstTaxesCall)).catch(
                  () => {
                      /* no op */
                  },
              )
            : Promise.resolve(),
    );

    const nextCheckQstTaxesTask = {
        id,
        promise,
    };

    lastCheckQstTaxesTask = nextCheckQstTaxesTask;

    return promise;
};

/**
 * @param {TaxExemptionStatus} status
 * @returns {(dispatch: Dispatch<any>) => Promise<void>}
 */
const revokeTaxExemptionWithMessage = (status) => async (dispatch) => {
    await dispatch(revokeTaxExemption());
    dispatch(updateTaxExemptionMessageAction(status));
};

/**
 * Validates current tax ID and country and updates the message provided to the user.
 */
export const checkTaxExemptionApplies = () => async (dispatch, getState) => {
    const state = getState();
    const {
        form: {
            checkoutForm: { values: { buyer = {}, paymentMethod } = {} } = {},
        },
    } = state;

    const isQuebecQSTEvent = getHasQuebecQSTTaxQuestion(state);
    if (isQuebecQSTEvent) {
        return dispatch(checkQstTaxesSequentially());
    }

    const isPayingWithPayPal =
        paymentMethod === PaymentMethodType.NONCE_BRAINTREE;

    if (isPayingWithPayPal) {
        return;
    }

    const taxId = buyer[CannedQuestionName.taxRegistrationId];

    const { country: countryTaxAddress = '' } =
        buyer[CannedQuestionName.businessTaxAddress] || {};

    const isBusiness = buyer[CannedQuestionName.isBusiness] === '1';

    const shouldRevokeTaxExemptions = !isBusiness || isEmpty(taxId);

    if (shouldRevokeTaxExemptions) {
        return dispatch(revokeTaxExemptionWithMessage(TaxExemptionStatus.init));
    }

    dispatch(updateTaxExemptionMessageAction(TaxExemptionStatus.verifying));

    const { is_tax_exempt, valid } = await validateTaxId(
        getEventId(getState()),
        countryTaxAddress,
        taxId,
    );

    if (!valid) {
        return dispatch(
            revokeTaxExemptionWithMessage(TaxExemptionStatus.invalidTaxId),
        );
    }

    if (!is_tax_exempt) {
        return dispatch(
            revokeTaxExemptionWithMessage(
                TaxExemptionStatus.validTaxIdWithoutTaxExemption,
            ),
        );
    }

    try {
        await dispatch(applyTaxExemption);
    } catch (error) {
        return dispatch(
            revokeTaxExemptionWithMessage(
                TaxExemptionStatus.validTaxIdWithoutTaxExemption,
            ),
        );
    }
};

export const getFormDataForTaxExemption = (state) => {
    const formValues = getFormValues(CHECKOUT_FORM_NAME)(state);
    const survey = getSurvey(state);
    let formDataForTaxExemption = cleanFormData(formValues, survey);

    // remove all buyer questions but tax related
    if (formDataForTaxExemption.buyer) {
        formDataForTaxExemption.buyer = pick(formDataForTaxExemption.buyer, [
            CannedQuestionName.isBusiness,
            CannedQuestionName.taxRegistrationId,
            CannedQuestionName.businessTaxAddress,
        ]);

        // Only in Quebec QST we have to send the billing zipcode as it's used to tell whether buyer is from Quebec
        // (and should be charged) or not
        if (getHasQuebecQSTTaxQuestion(state)) {
            const answers = getAnswersForQuestionsAffectingQuebecQSTTaxes(
                formValues,
                state,
            );
            formDataForTaxExemption = {
                ...formDataForTaxExemption,
                buyer: { ...formDataForTaxExemption.buyer, ...answers },
            };
        }
    }

    return formDataForTaxExemption;
};

export const saveOrderWithTaxExemption = () => {
    return (dispatch, getState) => {
        const state = getState();
        const orderId = orderSelectors.getOrderId(state.order);
        const deliveryMethods =
            getAttendeeDeliveryMethodsPayloadForPlacingOrder(state);
        const formData = getFormDataForTaxExemption(state);

        return saveOrderApi({
            orderId,
            formData,
            deliveryMethods,
        });
    };
};
